import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Form, Alert, OverlayTrigger, Tooltip, Button } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faQuestionCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/api';
import Ranked from '../components/Ciem/Ranked';
import CardLayout from '../components/Layout/CardLayout';

const CalculoCostoCapitalPage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [costoCapitalId, setCostoCapitalId] = useState(null);

    // Form data state
    const [formData, setFormData] = useState({
        valorInversionTotal: '',
        porcentajeCapitalSocios: '',
        valorInversionSocios: '',
        valorDeuda: '',
        costoDeuda: '',
        efectoImpuesto: '',
        tasaRetorno: '',
        costoCapital: '',
        roa: '',
        roaAcreedores: '',
        roaSocios: ''
    });

    const ciemId = useSelector(state => state.ciem.ciemId);
    const [rankedForm, setRankedForm] = useState([]);

    // Fetch data from API on component mount
    useEffect(() => {
        if (ciemId) {
            fetchCostoCapital();
        } else {
            setLoading(false);
        }

        axiosInstance.get('/detailoption/option/value/rankedTwelve').then((response) => {
            console.log("Calificación obtenida:", response.data.data[0]);
            setRankedForm(response.data.data[0]);
        }).catch((e) => {
            alert("Error consultando la calificacion " + e)
        });
    }, [ciemId]);

    const fetchCostoCapital = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/costoCapital/${ciemId}`);
            // Handle both direct object and { data: ... } wrapper patterns
            const responseData = response.data;
            // Some APIs return data directly, others wrap it in a 'data' property
            const data = (responseData && responseData.data && !responseData.id) ? responseData.data : responseData;

            console.log('Full response data:', responseData);
            console.log('Processed data object:', data);

            if (data && (data.id || data.valorInversionTotal)) {
                // Map API response to form data
                // Convert BigDecimal values to strings for form display
                console.log('Data received from API:', data);
                if (data.id) setCostoCapitalId(data.id);

                // Helper function to convert values to strings
                const toStr = (val) => {
                    if (val === null || val === undefined) return '';
                    if (typeof val === 'object') {
                        // Handle BigDecimal objects that might have a value property
                        return String(val.value !== undefined ? val.value : val);
                    }
                    return String(val);
                };

                const mappedData = {
                    valorInversionTotal: toStr(data.valorInversionTotal),
                    porcentajeCapitalSocios: toStr(data.porcentajeAporteSocios),
                    valorInversionSocios: toStr(data.valorInversionAportadaSocios),
                    valorDeuda: toStr(data.valorDeudaPorTomar),
                    costoDeuda: toStr(data.costoDeudaAnual),
                    efectoImpuesto: toStr(data.tasaImpuestoRenta),
                    tasaRetorno: toStr(data.tasaRetornoAnual),
                    costoCapital: toStr(data.costoCapitalAnual),
                    roa: toStr(data.retornoSobreInversion),
                    roaAcreedores: toStr(data.valorRoaAcreedores),
                    roaSocios: toStr(data.valorRoaSocios)
                };

                console.log('Mapped data for form:', mappedData);
                setFormData(mappedData);
            }

            setError(null);
        } catch (err) {
            // If 404, it means no data exists yet, which is fine
            if (err.response && err.response.status === 404) {
                setError(null);
            } else {
                setError('Error al cargar los datos: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: value
            };

            // Perform automatic calculations
            return calculateDerivedValues(updated);
        });
    };

    // Calculate all derived values based on input
    const calculateDerivedValues = (data) => {
        const result = { ...data };

        // Calculate Valor de la inversión aportada por socios (P)
        // P = V * (Porcentaje de capital aportado por socios / 100)
        if (data.valorInversionTotal && data.porcentajeCapitalSocios) {
            const V = parseFloat(data.valorInversionTotal) || 0;
            const porcentaje = parseFloat(data.porcentajeCapitalSocios) || 0;
            result.valorInversionSocios = (V * (porcentaje / 100)).toFixed(2);
        }

        // Calculate Valor deuda por tomar (D)
        // D = V - P
        if (data.valorInversionTotal && result.valorInversionSocios) {
            const V = parseFloat(data.valorInversionTotal) || 0;
            const P = parseFloat(result.valorInversionSocios) || 0;
            result.valorDeuda = (V - P).toFixed(2);
        }

        // Calculate Costo de capital (CK)
        // CK = [KD * (1 - t)] * (D / V) + Kp * (P / V)
        if (data.costoDeuda && data.efectoImpuesto && result.valorDeuda &&
            data.valorInversionTotal && data.tasaRetorno && result.valorInversionSocios) {

            const KD = parseFloat(data.costoDeuda) / 100 || 0;
            const t = parseFloat(data.efectoImpuesto) / 100 || 0;
            const D = parseFloat(result.valorDeuda) || 0;
            const V = parseFloat(data.valorInversionTotal) || 0;
            const Kp = parseFloat(data.tasaRetorno) / 100 || 0;
            const P = parseFloat(result.valorInversionSocios) || 0;

            if (V > 0) {
                const CK = (KD * (1 - t) * (D / V)) + (Kp * (P / V));
                result.costoCapital = (CK * 100).toFixed(2); // Convert back to percentage
            }
        }

        // Calculate Retorno sobre la inversión (ROA)
        // ROA = V * CK
        if (data.valorInversionTotal && result.costoCapital) {
            const V = parseFloat(data.valorInversionTotal) || 0;
            const CK = parseFloat(result.costoCapital) / 100 || 0;
            result.roa = (V * CK).toFixed(2);
        }

        // Calculate Valor del ROA para acreedores
        // ROA_acreedores = ROA * (D / V) * KD
        if (result.roa && result.valorDeuda && data.valorInversionTotal && data.costoDeuda) {
            const ROA = parseFloat(result.roa) || 0;
            const D = parseFloat(result.valorDeuda) || 0;
            const V = parseFloat(data.valorInversionTotal) || 0;
            const KD = parseFloat(data.costoDeuda) / 100 || 0;

            if (V > 0) {
                result.roaAcreedores = (ROA * (D / V) * KD).toFixed(2);
            }
        }

        // Calculate Valor del ROA para socios
        // ROA_socios = ROA * (P / V) * Kp
        if (result.roa && result.valorInversionSocios && data.valorInversionTotal && data.tasaRetorno) {
            const ROA = parseFloat(result.roa) || 0;
            const P = parseFloat(result.valorInversionSocios) || 0;
            const V = parseFloat(data.valorInversionTotal) || 0;
            const Kp = parseFloat(data.tasaRetorno) / 100 || 0;

            if (V > 0) {
                result.roaSocios = (ROA * (P / V) * Kp).toFixed(2);
            }
        }

        return result;
    };

    const handleSave = async () => {
        try {
            setError(null);
            setSuccess(null);

            // Map form data to API DTO
            const submitData = {
                id: costoCapitalId,
                ciemId: ciemId,
                valorInversionTotal: formData.valorInversionTotal ? parseFloat(formData.valorInversionTotal) : null,
                porcentajeAporteSocios: formData.porcentajeCapitalSocios ? parseFloat(formData.porcentajeCapitalSocios) : null,
                valorInversionAportadaSocios: formData.valorInversionSocios ? parseFloat(formData.valorInversionSocios) : null,
                valorDeudaPorTomar: formData.valorDeuda ? parseFloat(formData.valorDeuda) : null,
                costoDeudaAnual: formData.costoDeuda ? parseFloat(formData.costoDeuda) : null,
                tasaImpuestoRenta: formData.efectoImpuesto ? parseFloat(formData.efectoImpuesto) : null,
                tasaRetornoAnual: formData.tasaRetorno ? parseFloat(formData.tasaRetorno) : null,
                costoCapitalAnual: formData.costoCapital ? parseFloat(formData.costoCapital) : null,
                retornoSobreInversion: formData.roa ? parseFloat(formData.roa) : null,
                valorRoaAcreedores: formData.roaAcreedores ? parseFloat(formData.roaAcreedores) : null,
                valorRoaSocios: formData.roaSocios ? parseFloat(formData.roaSocios) : null
            };

            // API only uses POST for both create and update
            const response = await axiosInstance.post('/costoCapital/', submitData);
            if (response.data && response.data.id) {
                setCostoCapitalId(response.data.id);
            }

            if (costoCapitalId) {
                setSuccess('Datos actualizados correctamente');
            } else {
                setSuccess('Datos guardados correctamente');
            }

            // Refresh data
            fetchCostoCapital();
        } catch (err) {
            setError('Error al guardar los datos: ' + err.message);
        }
    };

    const formatCurrency = (value) => {
        if (!value) return '';
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para continuar.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="mb-0">
                                    <FontAwesomeIcon icon={faCalculator} className="me-2" />
                                    Cálculo del Costo de Capital
                                </h3>
                                <Button variant="primary" onClick={handleSave}>
                                    <FontAwesomeIcon icon={faSave} className="me-2" />
                                    Guardar
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                            {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : (
                                <Form>
                                    {/* Valor de la inversión total */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Valor de la inversión total (V):
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        Este valor proviene del módulo de "Inversión en capital de trabajo"
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="valorInversionTotal"
                                            value={formData.valorInversionTotal}
                                            onChange={handleInputChange}
                                            placeholder="Ingrese el valor de la inversión total"
                                        />
                                        {formData.valorInversionTotal && (
                                            <Form.Text className="text-muted">
                                                {formatCurrency(formData.valorInversionTotal)}
                                            </Form.Text>
                                        )}
                                    </Form.Group>

                                    {/* Porcentaje de capital aportado por los socios */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Porcentaje de capital aportado por los socios (%):
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        El porcentaje de capital aportado por los socios en un proyecto emprendedor por lo general debe ser mayor al 50% del monto total de la inversión total requerida, pues ello demuestra a las posibles entidades prestamistas que los Emprendedores e Inversores comprometidos en la idea emprendedora creen en su propia propuesta, por lo que están dispuestos a correr el mayor riesgo invirtiendo la mayor parte del capital requerido para financiarla. Usuario, introduzca el porcentaje que se espera aporten los socios para instalar la empresa.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="porcentajeCapitalSocios"
                                            value={formData.porcentajeCapitalSocios}
                                            onChange={handleInputChange}
                                            placeholder="Ingrese el porcentaje (ej: 60 para 60%)"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                        />
                                    </Form.Group>

                                    {/* Valor de la inversión aportada por socios */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Valor de la inversión aportada por socios (P):
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formatCurrency(formData.valorInversionSocios)}
                                            readOnly
                                            className="bg-light"
                                        />
                                        <Form.Text className="text-muted">
                                            Calculado automáticamente: V × Porcentaje de capital socios
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Valor deuda por tomar */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Valor deuda por tomar (D):
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formatCurrency(formData.valorDeuda)}
                                            readOnly
                                            className="bg-light"
                                        />
                                        <Form.Text className="text-muted">
                                            Calculado automáticamente: V - P
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Costo de la deuda (KD) anual */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Costo de la deuda (KD) anual (%):
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        KD es el costo de la deuda, dado como tasa de interés i. Ponga la tasa de interés nominal anual a la que espera tomar la deuda requerida para completar la financiación de la idea emprendedora. Si planea recurrir a dos o más entidades financieras prestamistas, KD será el promedio ponderado de la tasa de interés cobrado por las entidades, el cual se debe calcular como explica el "más información" de este módulo 15 costo de capital.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="costoDeuda"
                                            value={formData.costoDeuda}
                                            onChange={handleInputChange}
                                            placeholder="Ingrese la tasa de interés anual (ej: 12 para 12%)"
                                            step="0.01"
                                        />
                                    </Form.Group>

                                    {/* Efecto impuesto a la renta (1-t) */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Efecto impuesto a la renta - t (%):
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        (1-t) es el efecto del impuesto a la renta, donde 1 es un factor fijo, al que se resta t, que es el porcentaje o tasa de impuesto a la renta aplicado a las empresas en el país donde se instalará la empresa. Usuario, introduzca t.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="efectoImpuesto"
                                            value={formData.efectoImpuesto}
                                            onChange={handleInputChange}
                                            placeholder="Ingrese la tasa de impuesto (ej: 35 para 35%)"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                        />
                                    </Form.Group>

                                    {/* Tasa de retorno (Kp) anual */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Tasa de retorno (Kp) anual (%):
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        Kp es la tasa de retorno o el costo del patrimonio, es decir, el rendimiento que se espera ofrecer a los inversores para que decidan aportar recursos en la idea emprendedora. Usuario, ponga la tasa de retorno definida como se explicó en el módulo 14.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="tasaRetorno"
                                            value={formData.tasaRetorno}
                                            onChange={handleInputChange}
                                            placeholder="Ingrese la tasa de retorno anual (ej: 18 para 18%)"
                                            step="0.01"
                                        />
                                    </Form.Group>

                                    {/* Costo de capital (CK) anual */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Costo de capital (CK) anual (%):
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        CK es el costo de capital promedio ponderado obtenido de: CK = [KD × (1 - t)] × (D / V) + Kp × (P / V).
                                                        Donde KD es el costo de la deuda, t es el impuesto a la renta, D es el monto de la deuda, V es el valor de la inversión total, Kp es el costo del patrimonio, y P es el monto del patrimonio o aporte de socios.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.costoCapital ? `${formData.costoCapital}%` : ''}
                                            readOnly
                                            className="bg-light"
                                        />
                                        <Form.Text className="text-muted">
                                            Calculado automáticamente: [KD × (1 - t)] × (D / V) + Kp × (P / V)
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Retorno sobre la inversión (ROA) */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Retorno sobre la inversión (ROA):
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        ROA = IT × CK. El retorno sobre la inversión (ROA), indica el rendimiento mínimo que debe generar la inversión total (IT) realizada en un proyecto de inversión, lo que se obtiene multiplicando el monto calculado como IT por el costo de capital (CK). Ese rendimiento se distribuirá entre inversores y acreedores en proporción al porcentaje como cada uno de esos agentes se espera contribuya a financiar el modelo de negocio.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formatCurrency(formData.roa)}
                                            readOnly
                                            className="bg-light"
                                        />
                                        <Form.Text className="text-muted">
                                            Calculado automáticamente: V × CK
                                        </Form.Text>
                                    </Form.Group>

                                    <hr className="my-4" />

                                    <h5 className="mb-3">Distribución del ROA</h5>

                                    {/* Valor del ROA para acreedores */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Valor del ROA para acreedores:
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        Del valor del ROA les corresponde a los acreedores la proporción de la inversión total por ellos financiada, multiplicado ese valor por el costo de la deuda (KD), siendo pagado ello mediante intereses. A los acreedores se les debe pagar sus intereses (igualmente el abono al capital) sin importar si la empresa obtiene o no utilidades.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formatCurrency(formData.roaAcreedores)}
                                            readOnly
                                            className="bg-light"
                                        />
                                        <Form.Text className="text-muted">
                                            Calculado automáticamente: ROA × (D / V) × KD
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Valor del ROA para socios */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Valor del ROA para socios:
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        Del valor del ROA les corresponde a los socios o dueños la proporción de la inversión total por ellos financiada, la cual debe corresponder con la tasa de retorno (Kp) a ellos ofrecida, siendo pagada como distribución de utilidades o rendimiento de las acciones poseídas en la empresa, pero se realiza el pago siempre y cuando la empresa arroje al final de cada periodo contable flujo de caja libre disponible para socios positivo.
                                                    </Tooltip>
                                                }
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" size="sm" />
                                            </OverlayTrigger>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formatCurrency(formData.roaSocios)}
                                            readOnly
                                            className="bg-light"
                                        />
                                        <Form.Text className="text-muted">
                                            Calculado automáticamente: ROA × (P / V) × Kp
                                        </Form.Text>
                                    </Form.Group>
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Ranked - Calificación */}
            {(() => {
                const calificacion = <Ranked field={rankedForm} ciemId={ciemId} />;
                return <CardLayout value={"rankedTwelve"} tablaComponent={calificacion} />;
            })()}
        </Container>
    );
};

export default CalculoCostoCapitalPage;
